import api from '@/api/client';
import { servicePath } from '@/config/env';
import type {
  AppNotification,
  NotificationActor,
  NotificationTab,
  NotificationType,
} from '@/features/notifications/types';

// ---------------------------------------------------------------------------
// Raw API shapes (notification service v4)
// ---------------------------------------------------------------------------

interface RawNotificationUser {
  id: number;
  userTypeId?: number;
  name?: string;
  surName?: string;
  username?: string;
  profileImage?: string;
  profileImageThumbnail?: string;
}

interface RawJoinRequestBlock {
  joinFollowRequestCount?: number;
  joinBoardRequestCount?: number;
  userWhoFiredEvent?: RawNotificationUser;
  createdAt?: number;
}

interface RawNotificationItem {
  id: number;
  notificationTypeId: number;
  notificationType: string;
  objectDataId?: number;
  userWhoFiredEvent: RawNotificationUser;
  userToNotify?: RawNotificationUser;
  message?: string;
  isRead?: boolean;
  createdAt?: number;
}

interface RawNotificationsResponse {
  statusCode?: string;
  totalCount?: number;
  data?: {
    joinFollowRequest?: RawJoinRequestBlock;
    joinBoardRequest?: RawJoinRequestBlock;
    notifications?: RawNotificationItem[];
  };
}

interface RawMessagePayload {
  activityMsg?: string;
  commentText?: string;
  boardId?: number;
  boardName?: string;
}

// ---------------------------------------------------------------------------
// Public response
// ---------------------------------------------------------------------------

export interface NotificationsPendingCounts {
  joinFollowRequestCount: number;
  joinBoardRequestCount: number;
}

/**
 * Single "needs attention" summary returned by the notification service
 * (e.g. one entry that says "X people requested to follow you").
 */
export interface NotificationPendingSummary {
  count: number;
  latestActor: NotificationActor;
  createdAt: number;
}

export interface NotificationsResponse {
  you: AppNotification[];
  following: AppNotification[];
  unreadCount: number;
  totalCount: number;
  pending: NotificationsPendingCounts;
  joinFollowRequest: NotificationPendingSummary | null;
  joinBoardRequest: NotificationPendingSummary | null;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function initialsFrom(first?: string, last?: string): string {
  const a = (first ?? '').trim().charAt(0);
  const b = (last ?? '').trim().charAt(0);
  const out = `${a}${b}`.toUpperCase();
  return out || '?';
}

function toActor(user: RawNotificationUser | undefined): NotificationActor {
  const name = [user?.name, user?.surName].filter(Boolean).join(' ').trim() || 'Unknown';
  return {
    id: user?.id ?? 0,
    name,
    initials: initialsFrom(user?.name, user?.surName),
    username: user?.username,
    avatarUrl: user?.profileImageThumbnail || user?.profileImage || undefined,
  };
}

function parseMessage(message?: string): RawMessagePayload {
  if (!message) return {};
  try {
    return JSON.parse(message) as RawMessagePayload;
  } catch {
    return { activityMsg: message };
  }
}

/** Map backend `notificationType` to our internal taxonomy. */
function mapType(notificationType: string): NotificationType | null {
  switch (notificationType) {
    case 'NewsLike':
    case 'NewsLikeUserChain':
      return 'post_liked';
    case 'NewsCommentOwnerUser':
    case 'NewsCommentedUserChain':
      return 'post_commented';
    case 'NewsCommentMention':
      return 'post_mentioned';
    case 'JoinBoard':
      return 'class_joined';
    // The notification service has used different labels for the same
    // "a person you follow created a class" activity across deployments.
    case 'BoardCreate':
    case 'CreateBoard':
    case 'BoardCreated':
    case 'NewBoard':
    case 'BoardCreateUserChain':
    case 'BoardCreatedUserChain':
      return 'class_created';
    case 'PrivateBoardAcceptRequest':
      return 'class_admin_promoted';
    case 'AdminInvite':
      return 'class_admin_invited';
    case 'Follow':
      return 'user_followed';
    default:
      return null;
  }
}

function formatRelativeDate(ms?: number): string {
  if (!ms) return '';
  const date = new Date(ms);
  return date.toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function toAppNotification(raw: RawNotificationItem, tab: NotificationTab): AppNotification | null {
  const type = mapType(raw.notificationType);
  if (!type) return null;
  const payload = parseMessage(raw.message);
  return {
    id: raw.id,
    tab,
    type,
    actor: toActor(raw.userWhoFiredEvent),
    createdAt: formatRelativeDate(raw.createdAt),
    read: Boolean(raw.isRead),
    className: payload.boardName,
    postId: type.startsWith('post_') ? raw.objectDataId : undefined,
    boardId: payload.boardId ?? (type.startsWith('class_') ? raw.objectDataId : undefined),
    activityMsg: payload.activityMsg,
    commentText: payload.commentText,
    userToNotify: raw.userToNotify ? toActor(raw.userToNotify) : undefined,
  };
}

// ---------------------------------------------------------------------------
// Fetcher
// ---------------------------------------------------------------------------

export interface FetchNotificationsParams {
  userId: string | number;
  category?: 'you' | 'following';
  page?: number;
  size?: number;
}

/**
 * Fetch in-app notifications.
 *
 * - `you`        → `/notificationservice/api/v4/notification` (personal alerts +
 *                   pending class/follow requests).
 * - `following`  → `/midl/api/v3/notification` (chain-style activity from the
 *                   people / classes you follow).
 */
export async function fetchNotifications({
  userId,
  category = 'you',
  page = 0,
  size = 20,
}: FetchNotificationsParams): Promise<NotificationsResponse> {
  const url =
    category === 'following'
      ? servicePath('midl', 'notification', { version: 'v3' })
      : servicePath('notification', 'notification', { version: 'v4' });

  const { data } = await api.get<RawNotificationsResponse>(url, {
    params: {
      userId,
      notificationCategory: category,
      page,
      size,
    },
    // Notifications are an optional service and may not be provisioned yet
    // for a newly-created account. Its 401 must not invalidate the valid user
    // session established by userservice/updateinfo.
    meta: {
      suppressLogoutOn401: true,
      suppressGlobalErrorToast: true,
    },
  } as never);

  const tab: NotificationTab = category === 'following' ? 'following' : 'you';
  const items = (data?.data?.notifications ?? [])
    .map((raw) => toAppNotification(raw, tab))
    .filter((item): item is AppNotification => item !== null);

  const unreadCount = items.filter((n) => !n.read).length;

  const joinFollowBlock = data?.data?.joinFollowRequest;
  const joinBoardBlock = data?.data?.joinBoardRequest;

  const joinFollowRequest: NotificationPendingSummary | null =
    joinFollowBlock && (joinFollowBlock.joinFollowRequestCount ?? 0) > 0
      ? {
          count: joinFollowBlock.joinFollowRequestCount ?? 0,
          latestActor: toActor(joinFollowBlock.userWhoFiredEvent),
          createdAt: joinFollowBlock.createdAt ?? 0,
        }
      : null;

  const joinBoardRequest: NotificationPendingSummary | null =
    joinBoardBlock && (joinBoardBlock.joinBoardRequestCount ?? 0) > 0
      ? {
          count: joinBoardBlock.joinBoardRequestCount ?? 0,
          latestActor: toActor(joinBoardBlock.userWhoFiredEvent),
          createdAt: joinBoardBlock.createdAt ?? 0,
        }
      : null;

  return {
    you: tab === 'you' ? items : [],
    following: tab === 'following' ? items : [],
    unreadCount,
    totalCount: data?.totalCount ?? items.length,
    pending: {
      joinFollowRequestCount: joinFollowRequest?.count ?? 0,
      joinBoardRequestCount: joinBoardRequest?.count ?? 0,
    },
    joinFollowRequest,
    joinBoardRequest,
  };
}
